import type { AxiosError } from 'axios';
import Axios from 'axios';
import noop from 'lodash/noop';
import sum from 'lodash/sum';
import throttle from 'lodash/throttle';
import { combineAll } from '../async/combineAll';
import { asyncTimeout } from '../async/asyncTimeout';
import type { TaskPriority } from '../async/taskPriority';
import { TaskUrgency } from '../async/taskPriority';
import { makePriority, nextSerial, PrioritySemaphore } from '../async/prioritySemaphore';
import { ProgressBuilder, resilientlyConcludeTask } from '../async/progressBuilder';
import type { UploadProgressInfo, FileSetProgress, FileSetListener } from './uploadProgression';
import type { ProgressInfo } from '../dto/progress';
import { OperationConclusion } from '../dto/progress';
import type {
  CreateItemUploadLockFailureDto,
  CreateItemUploadLockRequestDto,
  CreateItemUploadLockResponseDto} from '../dto/uploadLocationDto';
import {
  DEFAULT_UPLOAD_LOCATION,
  isMultipartUploadResponseDto,
} from '../dto/uploadLocationDto';
import { exceptionToString, makeError } from '../util/exception';
import { createItemUploadLock } from '../api/uploadApi';
import { apiDeleteItem, apiUpdateItem } from '../util/httpApi';
import { invariant } from '../util/invariant';
import { isFileApi, type FileApi } from './fileApi';
import type { FilesystemApis } from './fileOps';
import { MultiPartUploader } from './multipartUploader';
import { SimpleUploader } from './simpleUploader';
import type { UploadManagerOptions} from './upload';
import { UploadState } from './upload';
import type { ItemMetaData } from '../dto/fieldValue';
import { createUuid } from '../util/createUuid';
import type { TaskContext } from '../async/taskContext';
import { runTask } from '../async/taskScope';
import type { CreateItemAttachmentRequestDto } from '../dto/itemAttachmentDto';
import { UPLOAD_MAX_PART_SIZE, UPLOAD_MULTIPART_THRESHOLD } from './limits';
import type { UploadMethods } from './upload-types';
import type { ItemCreationResponse } from '../dto/itemModels';
import type { BackendClient } from '../util/backendClient';
import { isInAdobeApp, isInElectron, type ApplicationContext } from '../context/applicationContext';
import { asyncSleep } from '../util/asyncSleep';
import { portableClock } from '../async/timing';
import { ExponentialBackoff } from '../async/resilience';

export interface Uploader {
  startUpload(): Promise<void>;
  cancel(): void;
}

export interface ManagedUploadItem {
  readonly id: string;
  readonly mediaType: string;
  readonly folderPath: string;
  readonly isCreated: boolean;
  readonly isExistingItem: boolean;
  readonly isCreating: boolean;

  metadata: ItemMetaData | null;
  useMultiPart: boolean;
  uploadState: UploadState;
  markedForDeletion: boolean;
  file: FileApi;
  uploader: Uploader | null;
  totalUploadedSize: number;
  uploadProgress: number;
  targetUploadLocationId?: string;
  bucketConfig?: CreateItemUploadLockResponseDto | undefined;

  getFilename(): string;
  beforeUpload(priority: TaskPriority): Promise<undefined | ItemCreationResponse>;
  onUploadError(uploadMethods?: UploadMethods): Promise<void>;
  onUploadCompleted(uploadMethods?: UploadMethods): Promise<void>;
  onMetadataChange(newMetadata: ItemMetaData, newChanges: boolean): void;
}

interface UploadReservation {
  response: CreateItemUploadLockResponseDto;
  useMultipart: boolean;
}

const uploadStateByOperationConclusion = {
  [OperationConclusion.PENDING]: UploadState.PROGRESSING,
  [OperationConclusion.SUCCEEDED]: UploadState.COMPLETE,
  [OperationConclusion.FAILED]: UploadState.ERROR,
  [OperationConclusion.CANCELLED]: UploadState.CANCELLED
};

export class BasicUploadManager {
  /*
    Our experience with P2 cards seems to indicate that reading multiple files
    in parallel from the file system causes slowdowns. Therefore use this
    semaphore to limit reads to one file at a time.
  */
  protected fsSemaphore = new PrioritySemaphore(1);

  /*
    While some amount of upload parallelism helps with upload performance since
    we avoid pipeline stalls, having too many makes the upload activity
    unpredictable, and run the risk of obtaining presigned URLs too early such
    that they expire before we have a chance of using them.

    A semaphore may not be the best choice for rate limiting in this scenario,
    since we want to limit the delay, rather than the concurrency, and higher
    bandwidth connections and smaller upload sizes will have lower latency than
    lower bandwidth and larger upload sizes at the same concurrency. High
    bandwidth and small upload size scenarios will also achieve higher
    throughput with higher concurrency values.

    TODO: Try alternative rate limiting based on delay feedback.

    The current concurrency value of 20 was selected as higher concurrency
    values exhibit diminishing throughput returns during testing.

    Test scenario: Web browser folder upload of 1000 images.

    Test results:

      | Concurrency | Throughput | Total time |
      | ----------- | ---------- | ---------- |
      |          10 |  4.31 MB/s |      1m38s |
      |          20 |  7.09 MB/s |      1m24s |
      |          40 |  7.26 MB/s |      1m22s |

  */
  private uploadSemaphore = new PrioritySemaphore(20);
  public uploadLockObtainedMap: Record<string, boolean> = {};

  constructor(protected applicationContext: ApplicationContext, public options: UploadManagerOptions, protected fsApis?: FilesystemApis) {
  }

  private async prepareItemForUpload(item: ManagedUploadItem, priority: TaskPriority) {
    if (!item.isExistingItem && (!item.isCreated || item.isCreating)) {
      await item.beforeUpload(priority);
    }
    item.useMultiPart = item.file.size >= UPLOAD_MULTIPART_THRESHOLD;
  }

  public async uploadFileBatchForItem(tc: TaskContext, itemIdFunc: () => string | null, fileItems: ManagedUploadItem[], listener: FileSetListener | null) {
    const priority: TaskPriority = makePriority({ urgency: TaskUrgency.LongRunning, serial: tc.priority.serial });
    return runTask({ parent: tc, priority}, (ctc) => this.uploadFileBatchForItemLongRunning(ctc, itemIdFunc, fileItems, listener));
  }


  public async uploadFileBatchForItemLongRunning(
    tc: TaskContext,
    itemIdFunc: () => string | null, // null until we have a created Item
    fileItems: ManagedUploadItem[],
    listener: FileSetListener | null
  ) {
    const throttledItemUpdate = throttle(
      (...args: Parameters<typeof apiUpdateItem>) => apiUpdateItem(...args).catch(err => this.options.logger.error(err)),
      5000
    );
    let conclusion: OperationConclusion | null  = null;
    let id: string | null;
    let errorCount = 0;

    const sublistener: FileSetListener = (progressInfo: FileSetProgress) => {
      if (listener)
        listener(progressInfo);

      const previousId = id;
      id = itemIdFunc();
      if (!id) {
        // No item created yet
        return;
      }
      const itemWasCreated = id !== previousId;
      const newErrorCount = progressInfo.errors.length;

      // Immediately update if any of these scenarios occur
      if (conclusion != progressInfo.conclusion || itemWasCreated || errorCount != newErrorCount) {
        conclusion = progressInfo.conclusion;
        errorCount = newErrorCount;

        this.options.logger.info(id + ' progress ' + JSON.stringify(progressInfo));

        let uploadProgressInfo: UploadProgressInfo | null = {
          bytes: progressInfo.bytes,
          files: progressInfo.files
        };

        // We need to clear upload progress info serverside in the case of cancelled state so other
        // clients don't continue to show items in progress.
        if (conclusion === OperationConclusion.CANCELLED) {
          uploadProgressInfo = null;
        }

        throttledItemUpdate.cancel();
        this.onConclusionChange({ id, progressInfo });

        const updateUploadProgress = () => apiUpdateItem(
          this.options.client,
          id!,
          { progressInfo, uploadMethods: progressInfo.uploadMethods, uploadProgressInfo },
          { priority: { ...tc.priority, urgency: TaskUrgency.Idle } },
        );

        updateUploadProgress()
          .then(response => this.onConclusionChange({ id: response.id, progressInfo }))
          .catch(ex => {
            if ([502, 503].includes(ex?.response?.status)) { // retryable
              void asyncTimeout(2000)
                .then(() =>
                  updateUploadProgress()
                    .then(response => this.onConclusionChange({ id: response.id, progressInfo }))
                    .catch(eex => this.options.logger.error(exceptionToString(eex))),);
            } else {
              this.options.logger.error(exceptionToString(ex));
            }
          });
      } else {
        // Otherwise, throttle an update to persist upload progress so far, every 5 seconds.
        const uploadInfo: UploadProgressInfo = {
          bytes: progressInfo.bytes,
          files: progressInfo.files
        };
        const itemProgressInfo: ProgressInfo = {
          status: progressInfo.status,
          description: progressInfo.description,
          errors: progressInfo.errors,
          conclusion: progressInfo.conclusion,
        };
        void throttledItemUpdate(
          this.options.client,
          id,
          {
            progressInfo: itemProgressInfo,
            uploadProgressInfo: uploadInfo,
            uploadMethods: progressInfo.uploadMethods,
          },
          { priority: { ...tc.priority, urgency: TaskUrgency.Idle } }
        );
      }
    };

    return this.uploadFileBatch(tc, fileItems, sublistener);
  }

  protected onUploadCancelled(_itemId: string) {

  }


  public async cancelUpload(item: ManagedUploadItem) {
    await this.cancelUploadInternal(item);
  }

  private async cancelUploadInternal(item: ManagedUploadItem) {
    if (item.id === undefined) {
      return;
    }

    item.markedForDeletion = true;
    const progressStatus = item.uploadProgress;

    item.uploadState = UploadState.CANCELLED;

    if (item.uploader) {
      item.uploader.cancel();
    }

    item.uploadProgress = progressStatus;
    if (item.isCreated && !item.isExistingItem) {
      await apiDeleteItem(this.options.client, item.id, true, false, undefined);
    }
  }

  /**
   * Promise based API for uploading a set of file items as a single batch
   * operation.
   *
   * The assumption is that if one file fails to upload, there is no point in
   * continuing to upload the remaining files.
   *
   * This API does not report status through the uploadState property of the
   * item, and does not call the onUploadError() method on the item.
   */
  public async uploadFileBatch(tc: TaskContext, fileItems: ManagedUploadItem[], listener: FileSetListener | null) {
    const progressBuilder = new ProgressBuilder({
      status: 'Preparing',
      percentage: 0,
      uploadMethods: {},
      description: 'Preparing files to upload',
      errors: [],
      files: {
        done: 0,
        known: fileItems.length
      },
      bytes: {
        done: 0,
        known: sum(fileItems.map(fi => fi.file.size))
      },
      conclusion: OperationConclusion.PENDING
    }, listener, tc.getCancelToken());

    progressBuilder.fire();

    await progressBuilder.concludeTask('Uploaded', 'Upload', async () => {
      await combineAll(
        fileItems.map(async (fi) => {
          // Prefer completing files in the order given.
          const subpriority: TaskPriority = { ...tc.priority, serial: nextSerial() };
          const deltaListener = progressBuilder.makeDeltaListener();

          /*
            TODO: Replace all UI parts that still require subitem progress with
            UIs that track upload operations as a whole, rather than individual
            files. Once this is done, use the deltaListener directly, instead of
            shimming in this subItemListener.

            The UIs include:
              * Activity page
              * Bell notification drop-down
              * Dialog warning against closing client before uploads complete.
          */
          const subItemListener: FileSetListener = (progress: FileSetProgress) => {
            deltaListener(progress);
            const itemState = uploadStateByOperationConclusion[progress.conclusion];
            if (itemState) {
              fi.uploadState = itemState;
            }
          };

          try {
            await runTask({ parent: tc, priority: subpriority, transientErrorHandler: progressBuilder }, (stc) => this.uploadItem(stc, fi, subItemListener));
            await fi.onUploadCompleted(progressBuilder.progress.uploadMethods);
          } catch (err) {
            this.options.logger.error(`Error during uploadFileBatch: ${exceptionToString(err)}`);
            await fi.onUploadError(progressBuilder.progress.uploadMethods).catch(ex => this.options.logger.error(exceptionToString(ex)));
            throw err;
          }
        })
      );
    });
  }

  /**
   * Promise based API for uploading a single file item.
   */
  private async uploadItem(tc: TaskContext, item: ManagedUploadItem, listener: FileSetListener  | null) {
    const progressBuilder = new ProgressBuilder({
      status: 'Preparing',
      percentage: 0,
      uploadMethods: {},
      description: 'Preparing ' + item.file.name + ' for upload',
      errors: [],
      conclusion: OperationConclusion.PENDING,
      files: {
        done: 0,
        known: 1,
      },
      bytes: {
        done: 0,
        known: item.file.size
      }
    }, listener, tc.cancelToken);
    progressBuilder.fire();

    await resilientlyConcludeTask(progressBuilder, tc, 'Uploaded file', 'Upload of file ' + item.file.name, () => {
      return this.performUploadItemTask(tc, item, progressBuilder);
    }, this.options.getLogger());
  }

  private async performUploadItemTask(tc: TaskContext, item: ManagedUploadItem, progressBuilder: ProgressBuilder) {
    const cancelToken = tc.getCancelToken();
    cancelToken.throwIfRequested();
    item.uploadState = UploadState.SEE_PROGRESS;
    this.uploadLockObtainedMap[item.id] = false;
    try {
      await this.prepareItemForUpload(item, tc.priority);

      cancelToken.throwIfRequested();
      progressBuilder.fireActivity('Prepared upload', 'Prepared file ' + item.file.name + ' for upload');

      const queuedSignal = new AbortController();
      void this.runQueuedUploadLoop(queuedSignal.signal, item, progressBuilder);
      await this.uploadSemaphore.limit(
        () => {
          queuedSignal.abort();
          return this.uploadSingleItemCore(tc, item, progressBuilder.makeDeltaListener());
        },
        tc.priority,
        tc.getCancelToken()
      );

      progressBuilder.progress.files.done = progressBuilder.progress.files.known;

      // TODO: Always delegate fine-grained byte progress.
      progressBuilder.progress.bytes.done = progressBuilder.progress.bytes.known;
    } catch (ex: any) {
      // we should try to delete the lock only when we obtained the lock ourselves
      if (this.uploadLockObtainedMap[item.id]) {
        try {
          await this.deleteItemUploadLock(item.id);
          this.uploadLockObtainedMap[item.id] = false;
        } catch (err) {
          progressBuilder.addErrors([exceptionToString(err)]);
        }
      }
      throw ex;
    }
  }

  // While an upload is queued waiting to be run by the uploadSemaphore,
  // we do an item update every hour or so to make it possible for other
  // clients to know that the upload has not been abandoned.
  private async runQueuedUploadLoop(
    queuedSignal: AbortSignal,
    item: ManagedUploadItem,
    progressBuilder: ProgressBuilder,
  ) {
    const oneHourMs = 60 * 60 * 1000;
    while (!queuedSignal.aborted) {
      try {
        // There may lots of queued upload items, so we
        // spread out the updates.
        const delay = oneHourMs + (Math.random() * oneHourMs);
        await asyncSleep(portableClock(), delay, queuedSignal);
        if (queuedSignal.aborted) {
          return;
        }
        progressBuilder.fireActivity('Prepared upload', 'Waiting for slot to upload ' + item.file.name);
      } catch (err) {
        if (!queuedSignal.aborted) {
          const logger = this.options.getLogger();
          logger.log({
            level: 'warn',
            message: 'Error in queued upload update loop',
            error: err
          });
        }
        return;
      }
    }
  }

  // Needed for uploading premiere project

  public async uploadDirect(uploadItem: ManagedUploadItem) {
    const priority = makePriority();
    const file = uploadItem.file;
    invariant(isFileApi(file), 'Upload item must have file');
    const fileItem = uploadItem as (typeof uploadItem & { file: FileApi });

    uploadItem.useMultiPart = file.size >= UPLOAD_MULTIPART_THRESHOLD;

    // Store the local ID before it gets replaced with real Mimir GUID
    const localId = uploadItem.id;

    this.onUploadStart(uploadItem);

    await uploadItem.beforeUpload(priority);

    this.onUpdateUploadItemId(localId, uploadItem);

    await runTask({ priority, resilience: new ExponentialBackoff() }, async (tc) => {
      try {
        await this.uploadSingleItem(tc, fileItem);
        this.onUploadComplete(uploadItem.id, file.size);
      } catch (error) {
        this.onUploadError(uploadItem.id, error);
        throw error;
      }
    });
  }

  protected onUpdateUploadItemId(_localId: string, _uploadItem: ManagedUploadItem) {

  }

  protected onUploadStart(_uploadItem: ManagedUploadItem) {
  }

  protected onUploadComplete(_itemId: string, _fileSize: number) {
  }

  protected onUploadError(_itemId: string, _error: any) {
  }

  protected onConclusionChange(_update: { id: string, progressInfo: ProgressInfo}) {
    // Implement in subclass to be able to process conclusion change updates to application state
  }

  protected reportBriefError(_message: string) {
    // Implement in subclass to integrate with UI error reporting.
  }

  private async reportItemError(item: ManagedUploadItem, reason: Error) {
    // Use try-catch to prevent reporting errors from masking underlying errors.
    try {
      this.reportBriefError(reason.message);
    } catch (err) {
      this.options.logger.error(`Error while reporting brief error ${exceptionToString(err)}`);
    }

    try {
      await item.onUploadError();
    } catch (err) {
      this.options.logger.error(`Error while reporting upload error ${exceptionToString(err)}`);
    }
  }

  private async reserveUpload(itemId: string, lockRequest: CreateItemUploadLockRequestDto, priority: TaskPriority): Promise<UploadReservation> {
    try {
      const lockResponse = await createItemUploadLock(this.options.client, itemId, lockRequest, { priority });
      this.uploadLockObtainedMap[itemId] = true;
      return { response: lockResponse, useMultipart: lockRequest.isMultiPartUpload ?? false };
    } catch(ex) {
      const err = ex as any;
      const failure: Partial<CreateItemUploadLockFailureDto> = err?.response?.data?.error?.payload ?? {};
      const s3CloneFailure = failure.s3CloneFailure;

      if (!s3CloneFailure)
        throw err;

      /*
        Server was unable to communicate with S3 Gateway in order to prepare a
        multi-part upload. This may be because the server has no network
        connectivity towards the S3 Gateway. E.g. the server is in the cloud,
        while the S3 Gateway is behind a firewall at an on-premise location.

        TODO: We should get rid of this completely, since our product doesn't support
        unreachable S3 Gateways, and maintaining custom code paths for the purpose
        of testing causes our testing to not test the actual product behavior.
      */
      this.options.logger.info('Server was unable or unwilling to prepare multi-part upload by talking to S3 Gateway endpoint ' +
        s3CloneFailure.bucketEndpoint +
        '. Falling back to single part upload in case client has better connectivity to S3 Gateway endpoint than the API host.');
    }

    const singlePartLockRequest: CreateItemUploadLockRequestDto = {
      ...lockRequest,
      isMultiPartUpload: false,
      chunkSize: (lockRequest.fileSize ?? Infinity) + 1,
    };

    const response = await createItemUploadLock(this.options.client, itemId, singlePartLockRequest, { priority });
    this.uploadLockObtainedMap[itemId] = true;
    return { response, useMultipart: false };
  }

  /*
    The core implementation of upload single item, which does not itself change
    the item state (except for progress reporting), nor communicates with UIs or
    listeners.

    Returns a promise that resolves on successful upload, or rejects on failure
    or cancellation. Use Axios.isCancel(ex) to differentiate between failure and
    cancellation.

    If you want the UI and listeners to be called, use the wrapper function
    uploadSingleItem() instead.
  */
  protected async uploadSingleItemCore(tc: TaskContext, item: ManagedUploadItem, listener: FileSetListener  | null) {
    tc.getCancelToken().throwIfRequested();

    /*
      Step 1:

      Ask server to:
      * decide what the S3 targetKey or equivalent local file path should be.
      * lock the item for uploading by others.

      If we're doing S3 upload, we want the server to create a preauthenticated
      upload URL for us as well.

      If we're both doing S3 upload, and the file is large, we want the server
      to obtain an uploadId for a multipart upload.

      Before we can ask the server to do any of this, we need to determine
      whether we're doing an S3 upload or an upload through a filesystem copy.
    */

    const uploadDir = this.options.getUploadDirectory ? await this.options.getUploadDirectory() : null;
    const uploadDirExists = uploadDir != null && this.fsApis?.preferred != null && await this.fsApis.preferred.isDirectory(uploadDir);
    const file = item.file;
    const canCopyFileFromSource = file.path && this.fsApis?.preferred != null;
    const willDoFilesystemCopy = uploadDirExists && canCopyFileFromSource;

    const isMultiPartUpload = (!willDoFilesystemCopy) && (item.file.size >= UPLOAD_MULTIPART_THRESHOLD);
    const lockRequest: CreateItemUploadLockRequestDto = {
      lockOwnerInstanceId: this.options.getThisClientInstanceId(),
      chunkSize: UPLOAD_MAX_PART_SIZE,
      isMultiPartUpload,
      uploadLocationId: item.targetUploadLocationId ?? DEFAULT_UPLOAD_LOCATION,
      fileSize: item.file.size,
      contentType: item.mediaType,
      targetFolder: item.folderPath,
      fileName: item.getFilename()
    };

    const reserved = await this.reserveUpload(item.id, lockRequest, tc.priority);
    tc.getCancelToken().throwIfRequested();

    const bucketConfig: CreateItemUploadLockResponseDto = reserved.response;
    item.useMultiPart = reserved.useMultipart;

    // Step 2: Notify item about to upload
    item.bucketConfig = bucketConfig;

    // Notify item about to upload
    await item.beforeUpload(tc.priority);

    // Step 3: Create an uploader
    if (willDoFilesystemCopy) {
      const ctx = this.applicationContext;
      invariant(isInAdobeApp(ctx) || isInElectron(ctx), 'Should not have willDoFilesystemCopy === true outside Adobe panel or Electron context');
      item.uploader = {
        startUpload: async () => {
          const preferred = this.fsApis?.preferred;
          invariant(preferred, 'Should not have attempted file copy, missing preferred file ops');
          invariant(file.path, 'Should not have attempted file copy, missing path');
          invariant(uploadDir, 'Should not have attempted file copy, missing uploadDir');
          invariant(bucketConfig?.targetKey, 'Should not have attempted file copy, targetKey');

          const src = preferred.normalize(file.path);
          const targetPath = preferred.normalize(preferred.join(uploadDir, ...(bucketConfig.targetKey!.split('/'))));
          if (src === targetPath) {
            this.options.logger.info(`Skipping local fs copy since file is already at destination: ${targetPath}`);
            return;
          }

          const ct = tc.getCancelToken();
          await this.fsSemaphore.limit(async () => {
            // eslint-disable-next-line @typescript-eslint/unbound-method
            invariant(this.options.copyFileOptimizedForDesktop, 'copyFileOptimizedForDesktop not specified');

            const executionId = createUuid();

            // When this is cancelled in renderer, let the main process know, so that it can cancel
            // the actual ongoing copy operation through another token.
            ct.promise.then(() => {
              if (this.options.cancelCopyFileOptimizedForDesktop) {
                this.options.cancelCopyFileOptimizedForDesktop(ctx, executionId);
              }
            }).catch((err) => {
              this.options.logger.error(`Error cancelling copy execution ${executionId}: ${err}`);
            });

            const startMs = new Date().getTime();
            invariant(listener, 'Must set a listener when doing local file copy for reporting progress');
            const copyResult = await this.options.copyFileOptimizedForDesktop(ctx, {
              executionId,
              preferred,
              src,
              cancelToken: ct,
              logger: this.options.logger,
              dest: targetPath,
              title: file.name ?? src,
              itemId: item.id,
              knownSize: file.size ?? 0,
              listener,
            });
            const duration = (new Date().getTime() - startMs) / 1000;
            this.options.logger.info(`Took ${duration} seconds to copy ${src} to ${targetPath}`);

            if (copyResult && copyResult.wasCancelled) {
              this.options.logger.info('local path copy was cancelled');
              ct.throwIfRequested();
            }
          }, tc.priority, tc.cancelToken);
        },
        cancel: () => {}
      };
    } else {
      const uploader = this.createHttpUploader(item, bucketConfig, listener, tc);
      item.uploader = uploader;
    }

    void tc.cancelToken?.promise?.then(() => item.uploader?.cancel()).catch(noop);

    item.uploadState = UploadState.PROGRESSING;
    await item.uploader?.startUpload();
  }

  private createHttpUploader(
    item: ManagedUploadItem,
    bucketConfig: CreateItemUploadLockResponseDto,
    listener: FileSetListener | null,
    tc: TaskContext
  ): Uploader {
    const itemProgressListener: FileSetListener = (progress: FileSetProgress) => {
      const bytes = progress.bytes;
      item.totalUploadedSize = bytes.done;
      const percentCompleted = Math.floor(bytes.done * 100 / bytes.known);
      if (percentCompleted > item.uploadProgress) {
        item.uploadProgress = percentCompleted;
      }
      listener?.(progress);
    };

    if (item.useMultiPart) {
      if (!isMultipartUploadResponseDto(bucketConfig)) {
        throw new Error('unusable bucket for multipart file upload');
      }
      /*
        We reuse the same resilience object for individual part uploads as we
        use for each file as a whole. This prevents needlessly uploading parts
        that have already succeeded, as well as preventing the retry
        amplification that would occur if each multi-part upload got their own
        fresh retry state each time a file upload is started.
      */
      return new MultiPartUploader(
        this.options,
        item.id,
        bucketConfig,
        item.file,
        this.fsApis,
        this.fsSemaphore,
        itemProgressListener, tc
      );
    } else if (bucketConfig.uploadSignedUrl) {
      return new SimpleUploader(
        this.options.axios,
        bucketConfig.uploadSignedUrl,
        item.file,
        item.id,
        this.fsApis,
        this.fsSemaphore,
        itemProgressListener,
        tc.priority,
        this.options.getLogger()
      );
    } else {
      throw new Error('unusable bucket for file upload');
    }
  }

  protected async uploadSingleItem(tc: TaskContext, item: ManagedUploadItem) {
    try {
      await this.uploadSingleItemCore(tc, item, null);
      await item.onUploadCompleted();
      item.uploadState = UploadState.COMPLETE;
    } catch (ex) {
      if (Axios.isCancel(ex)) {
        item.uploadState = UploadState.CANCELLED;
      } else {
        /*
          If upload state was not cancelled, we can assume the error was
          unintentional and we report it.
        */

        if ((ex as Partial<AxiosError>).code == 'ECONNABORTED') {
          /*
            To help us troubleshoot unexpected errors. We may remove this later if
            we introduce higher level error handling.
          */
          this.options.logger.error(exceptionToString(ex));
        }

        if (item.uploadState !== UploadState.CANCELLED) {

          // If not already reported, report item error now.
          if (item.uploadState != UploadState.ERROR) {
            item.uploadState = UploadState.ERROR;

            await this.reportItemError(item, makeError('Could not upload file ' + item.file.name, 'UploadError', { cause: ex }));
          }

          invariant(item.uploadState == UploadState.ERROR, 'At this point we expect error');
        }
      }

      try {
        await this.deleteItemUploadLock(item.id);
      } catch (err) {
        this.options.logger.error(`Error deleting upload lock ${exceptionToString(err)}`);
      }
    }
  }

  protected deleteItemUploadLock(_itemId: string): Promise<void> {
    // Implement in subclass to integrate with UI code.

    // TODO: Make upload manager capable of deleting item upload locks by
    // itself.
    return Promise.resolve();
  }

  public async uploadAttachmentToItem(
    itemId: string,
    attachmentCreateRequest: CreateItemAttachmentRequestDto,
    fileToUpload: FileApi,
    createAttachmentApiCall: (
      client: BackendClient, itemId: string, payload: CreateItemAttachmentRequestDto
    ) => Promise<string>,
    onUploadProgress: (percentage: number) => void
  ) {
    const attachmentUploadUrl = await createAttachmentApiCall(this.options.client, itemId, attachmentCreateRequest);

    if (!attachmentUploadUrl) throw new Error('Could not create an attachment to item');

    // TODO: Consider what kind of resilience we want here.
    const percentListener: FileSetListener = (progress) => {
      const p = Math.floor(progress.bytes.done * 100 / progress.bytes.known);
      onUploadProgress(p);
    };
    const simpleUploader = new SimpleUploader(
      this.options.axios, attachmentUploadUrl, fileToUpload, itemId, this.fsApis, this.fsSemaphore, percentListener, makePriority(), this.options.getLogger()
    );
    await simpleUploader.startUpload();
  }
}
